import { Box, Skeleton, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { numSx } from '../../../../components/common/page/numberStyles';
import {
    changeColor,
    formatChange,
    formatChangeNumber,
    spanPhrase,
} from './changeOverRange';
import { formatValue } from './format';
import type { ChartSeries, Unit } from './seriesTypes';
import {
    formatStripTime,
    sharedUnit,
    sinceText,
    stripRows,
    type StripRow,
} from './valuesStrip';

// Below 900 px this sits above the chart and replaces the floating tooltip
// and the name chips: each visible outcome's value and its change over the
// shown range; while a finger is on the chart, the values at that moment and
// the change since the range start.

// "Now" while the right edge of the range is the live edge, a time otherwise
// (a closed market ends in the past).
const LIVE_EDGE_MS = 30 * 60_000;

interface ChartValuesStripProps {
    series: readonly ChartSeries[];
    domain: readonly [number, number];
    /** The whole span the chart holds data for: the rows come from its end (see stripRows). */
    loaded?: readonly [number, number];
    cursor: number | null;
    /** "1D", "All", "5d": the pressed range button or the length of the range. */
    rangeLabel: string;
    now?: number;
    /**
     * The rows the strip will hold once every series is in: missing ones are
     * drawn as placeholders, so the strip does not grow as they arrive.
     */
    minRows?: number;
}

const STRIP_SX = {
    mb: 1,
    px: '10px',
    py: 1,
    borderRadius: '8px',
    // a white card on the grey chart card; the dark theme keeps its default
    // background
    bgcolor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? theme.palette.common.white
            : theme.palette.background.default,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    // the mockup's rows are as tall as their text
    '& .MuiTypography-root': { lineHeight: 'normal' },
} as const;

const ROWS_SX = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: 1.75,
    rowGap: '3px',
} as const;

function StripRowView({ row, unit }: { row: StripRow; unit: Unit | null }) {
    const { change } = row;
    return (
        <Box
            data-testid="strip-row"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                minWidth: 0,
            }}
        >
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: row.color,
                    flexShrink: 0,
                }}
            />
            <Typography
                title={row.label}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: '13px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {row.label}
            </Typography>
            <Typography
                sx={{
                    fontSize: '13px',
                    fontWeight: 700,
                    flexShrink: 0,
                    ...numSx,
                }}
            >
                {row.value === null ? '–' : formatValue(row.unit, row.value)}
            </Typography>
            <Typography
                data-testid="strip-change"
                sx={{
                    minWidth: 38,
                    textAlign: 'right',
                    fontSize: '12px',
                    fontWeight: 500,
                    flexShrink: 0,
                    color: changeColor(change),
                    ...numSx,
                }}
            >
                {change
                    ? unit
                        ? formatChangeNumber(change)
                        : formatChange(change)
                    : '–'}
            </Typography>
        </Box>
    );
}

export function ChartValuesStrip({
    series,
    domain,
    loaded,
    cursor,
    rangeLabel,
    now = Date.now(),
    minRows = 0,
}: ChartValuesStripProps) {
    const rows = stripRows(series, domain, cursor, loaded);
    if (rows.length === 0) {
        return minRows > 0 ? (
            <ChartValuesStripPlaceholder rows={minRows} />
        ) : null;
    }

    const unit = sharedUnit(rows);
    const unitText =
        unit === 'probability'
            ? ', in points'
            : unit === 'price'
              ? ', in %'
              : '';
    const caption =
        cursor === null
            ? `change over ${spanPhrase(rangeLabel)}${unitText}`
            : `change since ${sinceText(domain[0], domain[1] - domain[0])}${unitText}`;
    const time =
        cursor !== null
            ? formatStripTime(cursor)
            : Math.abs(now - domain[1]) <= LIVE_EDGE_MS
              ? 'Now'
              : formatStripTime(domain[1]);

    return (
        <Box
            data-testid="values-strip"
            role="group"
            aria-label="Values"
            sx={{
                ...STRIP_SX,
                boxShadow:
                    cursor === null
                        ? 'none'
                        : theme =>
                              `inset 0 0 0 2px ${theme.palette.primary.main}`,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1,
                    // one line, always: the chart below must not move under a finger
                    whiteSpace: 'nowrap',
                }}
            >
                <Typography
                    data-testid="values-strip-time"
                    component="span"
                    sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        flexShrink: 0,
                        fontFamily: cursor === null ? undefined : 'monospace',
                        color:
                            cursor === null ? 'text.secondary' : 'primary.main',
                    }}
                >
                    {time}
                </Typography>
                <Typography
                    data-testid="values-strip-caption"
                    component="span"
                    sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: 'text.secondary',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {caption}
                </Typography>
            </Box>
            <Box sx={ROWS_SX}>
                {rows.map(row => (
                    <StripRowView key={row.id} row={row} unit={unit} />
                ))}
                {Array.from(
                    { length: Math.max(0, minRows - rows.length) },
                    (_, i) => (
                        <PlaceholderRow key={`placeholder-${i}`} />
                    )
                )}
            </Box>
        </Box>
    );
}

/**
 * The strip as it will stand once the chart's prices are in: the same box,
 * caption line and `rows` rows, with placeholders for the text, so the chart
 * below it does not move when the values arrive.
 */
function PlaceholderText({
    fontSize,
    stand,
}: {
    fontSize: string;
    stand: string;
}) {
    return (
        // as a flex box it is as tall as the text it stands for
        <Skeleton sx={{ display: 'flex' }}>
            <Typography component="span" sx={{ fontSize, fontWeight: 500 }}>
                {stand}
            </Typography>
        </Skeleton>
    );
}

function PlaceholderRow() {
    return (
        <Box
            data-testid="strip-row-placeholder"
            sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}
        >
            <PlaceholderText fontSize="13px" stand="Outcome name" />
        </Box>
    );
}

export function ChartValuesStripPlaceholder({ rows }: { rows: number }) {
    if (rows <= 0) return null;
    return (
        <Box data-testid="values-strip-placeholder" aria-hidden sx={STRIP_SX}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1,
                    whiteSpace: 'nowrap',
                }}
            >
                <PlaceholderText fontSize="12px" stand="Now" />
                <PlaceholderText
                    fontSize="12px"
                    stand="change over all time, in points"
                />
            </Box>
            <Box sx={ROWS_SX}>
                {Array.from({ length: rows }, (_, i) => (
                    <PlaceholderRow key={i} />
                ))}
            </Box>
        </Box>
    );
}
